// frontend/src/components/Layout/Layout.js
import React, { useState, Children, cloneElement, isValidElement, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import Sidebar from './SidebarWrapper';
import { useTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useProjectModal } from '../../contexts/ProjectModalContext';
import { useUsersPermissions } from '../../contexts/UsersPermissionsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useProjectPageTracker } from '@navigation/hooks';
import UsersPermissionsDialog from '../Dashboard/UsersPermissionsDialog';

// MUI components for the modal
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import RightSidebar from './RightSidebar';
import CloseIcon from "@mui/icons-material/Close";

const drawerWidth = 83; // Changed from 250
const collapsedDrawerWidth = 60;

const getPageTitle = (pathname) => {
  if (pathname.startsWith('/project/')) return "Project Details";
  if (pathname === '/all-projects') return "Dashboard";
  return "Audience Persona Platform";
};

const hideSideBarPathNames = ['/all-projects'];

function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    isCreateProjectModalOpen,
    closeCreateProjectModal,
    newProjectName,
    setNewProjectName,
    formError,
    handleCreateProject,
    isSubmitting,
    newlyCreatedProjectId,
    clearNewlyCreatedProjectId
  } = useProjectModal();

  const { isDialogOpen, currentProject, closeUsersPermissionsDialog } = useUsersPermissions();
  const { currentUser } = useAuth();

  // Track last visited project page for non-admins
  useProjectPageTracker();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const pageTitle = getPageTitle(location.pathname);

  // Effect to navigate after project creation
  useEffect(() => {
    if (newlyCreatedProjectId) {
      navigate(`/project/${newlyCreatedProjectId}`);
      clearNewlyCreatedProjectId(); // Clear the ID after navigation
    }
  }, [newlyCreatedProjectId, navigate, clearNewlyCreatedProjectId]);

  return (
    <Box sx={{ display: 'flex' }}>
      {!hideSideBarPathNames.includes(location.pathname) &&
        <>
          <CssBaseline />
          <AppBar
            position="fixed"
            sx={{
              display: { xs: 'block', sm: 'none' },
              boxShadow: theme.shadows[1],
            }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { sm: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div">
                {pageTitle} 
              </Typography>
            </Toolbar>
          </AppBar>
          
          <Sidebar 
            mobileOpen={mobileOpen} 
            handleDrawerToggle={handleDrawerToggle} 
            sidebarOpen={sidebarOpen} 
            handleSidebarToggle={handleSidebarToggle} 
          />
        </>
      }
      
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          width: { sm: `calc(100% - ${sidebarOpen ? drawerWidth+16 : collapsedDrawerWidth+16}px)` },
          p: 0, // Remove default padding
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          maxHeight:'100vh',
          overflow:'auto',
        }}
      >
        {!hideSideBarPathNames.includes(location.pathname) && <Toolbar sx={{ display: { xs: 'block', sm: 'none' } }} />}
        {Children.map(children, child => {
          if (isValidElement(child)) {
            const propsToPass = { sidebarOpen };
            return cloneElement(child, propsToPass);
          }
          return child;
        })}
      </Box>

      {/* Create Project Modal - Now part of Layout */}
      <Dialog
        open={isCreateProjectModalOpen}
        fullWidth
        maxWidth="xs"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            padding: '0 0 8px',
          }}
        >
          Create New Project
          <IconButton
            onClick={() => {
              if (!isSubmitting) {
                closeCreateProjectModal();
              }
            }}
            size="small"
            sx={{
              backgroundColor: (theme) => theme.palette.action.hover,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: '0',
            my: 2,
          }}
        >
          <DialogContentText sx={{ mb: 1 }}>
            Enter the name for your new project.
          </DialogContentText>
          {formError && <Typography color="error" sx={{ mb: 1, fontSize: '0.9rem' }}>{formError}</Typography>}
          <TextField
            autoFocus
            margin="dense"
            id="layout-new-project-name"
            label="Project Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            required
            disabled={isSubmitting}
            onKeyPress={(e) => e.key === 'Enter' && !isSubmitting && handleCreateProject()}
          />
        </DialogContent>
        <DialogActions sx={{ p: '16px 0 0' }}>
          <Button onClick={handleCreateProject} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Users & Permissions Dialog */}
      {currentProject?.id && (
        <UsersPermissionsDialog
          open={isDialogOpen}
          onClose={closeUsersPermissionsDialog}
          projectId={currentProject.id}
          projectName={currentProject.projectName}
          currentUser={currentUser}
        />
      )}

      {/* Right sidebar */}
      {!hideSideBarPathNames.includes(location.pathname) && <RightSidebar />}
    </Box>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Layout;